/**
 * BeoordelingPopover
 *
 * Click-to-open popover for setting the beoordeling (assessment) of an incasso dossier.
 * Trigger is a colored dot with tooltip on hover. Content shows 3 options and a "clear" action.
 *
 * - NIET_BEREIKBAAR: saves immediately (no reason needed)
 * - KOMT_IN_ORDE / KOMT_NIET_IN_ORDE: shows inline textarea for reason, then confirms
 * - Tooltip shows beoordeling label + reason (if set)
 */

import { useState } from 'react';
import { Check } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { Textarea } from 'components/ui/textarea';
import { Button } from 'components/ui/button';
import { cn } from '@/lib/utils';
import { IncassoBeoordeling } from '../shared/incassoTypes';

interface BeoordelingOption {
  value: IncassoBeoordeling;
  label: string;
  dotColor: string;
  needsReason: boolean;
}

const BEOORDELING_OPTIONS: BeoordelingOption[] = [
  {
    value: IncassoBeoordeling.KOMT_IN_ORDE,
    label: 'Komt wellicht in orde',
    dotColor: 'bg-green-500',
    needsReason: true,
  },
  {
    value: IncassoBeoordeling.NIET_BEREIKBAAR,
    label: 'Kan niet bereiken',
    dotColor: 'bg-amber-400',
    needsReason: false,
  },
  {
    value: IncassoBeoordeling.KOMT_NIET_IN_ORDE,
    label: 'Komt niet in orde',
    dotColor: 'bg-red-500',
    needsReason: true,
  },
];

/** Get dot color class for a beoordeling value */
export const getBeoordelingDotColor = (value: IncassoBeoordeling | null): string => {
  if (!value) return 'bg-neutral-300';
  const option = BEOORDELING_OPTIONS.find((o) => o.value === value);
  return option?.dotColor ?? 'bg-neutral-300';
};

/** Get label for a beoordeling value */
export const getBeoordelingLabel = (value: IncassoBeoordeling | null): string => {
  if (!value) return 'Geen beoordeling';
  const option = BEOORDELING_OPTIONS.find((o) => o.value === value);
  return option?.label ?? 'Onbekend';
};

interface BeoordelingPopoverProps {
  value: IncassoBeoordeling | null;
  /** Current reason text (shown in tooltip) */
  reden?: string | null;
  onChange: (v: IncassoBeoordeling | null, reden?: string) => void;
  disabled?: boolean;
  size?: number;
}

export const BeoordelingPopover = ({
  value,
  reden,
  onChange,
  disabled = false,
  size = 10,
}: BeoordelingPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<IncassoBeoordeling | null>(null);
  const [redenText, setRedenText] = useState('');

  const handleSelect = (beoordeling: IncassoBeoordeling) => {
    const option = BEOORDELING_OPTIONS.find((o) => o.value === beoordeling);

    if (option?.needsReason) {
      // Show textarea for reason input
      setPendingSelection(beoordeling);
      setRedenText('');
    } else {
      // Save immediately (NIET_BEREIKBAAR)
      try {
        onChange(beoordeling);
      } catch (error) {
        console.error('[BeoordelingPopover] Failed to update beoordeling:', error);
        throw error;
      }
      resetAndClose();
    }
  };

  const handleConfirmWithReason = () => {
    if (!pendingSelection || !redenText.trim()) return;
    try {
      onChange(pendingSelection, redenText.trim());
    } catch (error) {
      console.error('[BeoordelingPopover] Failed to update beoordeling:', error);
      throw error;
    }
    resetAndClose();
  };

  const handleClear = () => {
    try {
      onChange(null);
    } catch (error) {
      console.error('[BeoordelingPopover] Failed to clear beoordeling:', error);
      throw error;
    }
    resetAndClose();
  };

  const handleBack = () => {
    setPendingSelection(null);
    setRedenText('');
  };

  const resetAndClose = () => {
    setPendingSelection(null);
    setRedenText('');
    setIsOpen(false);
  };

  const dotColor = getBeoordelingDotColor(value);
  const label = getBeoordelingLabel(value);
  const tooltipText = reden ? `${label}: ${reden}` : label;

  const dot = (
    <div
      className={cn('rounded-full shrink-0 inline-block', dotColor)}
      style={{ width: size, height: size }}
      aria-label={label}
    />
  );

  if (disabled) {
    if (!value) return dot;
    return (
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>{dot}</TooltipTrigger>
          <TooltipContent side="top">
            <p className="max-w-[200px]">{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={(open) => {
      setIsOpen(open);
      if (!open) {
        setPendingSelection(null);
        setRedenText('');
      }
    }}>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="rounded-full focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-moss-primary"
                aria-label="Beoordeling instellen"
              >
                {dot}
              </button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent side="top">
            <p className="max-w-[200px]">{tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent
        className={cn('p-1', pendingSelection ? 'w-64' : 'w-48')}
        align="start"
        side="bottom"
        onKeyDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {pendingSelection ? (
          /* ─── Reason input step ─────────────────────────────── */
          <div className="flex flex-col gap-2 p-2">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'w-2.5 h-2.5 rounded-full shrink-0',
                  BEOORDELING_OPTIONS.find((o) => o.value === pendingSelection)?.dotColor,
                )}
              />
              <span className="text-sm font-medium text-neutral-900">
                {BEOORDELING_OPTIONS.find((o) => o.value === pendingSelection)?.label}
              </span>
            </div>
            <Textarea
              placeholder="Waarom?"
              value={redenText}
              onChange={(e) => setRedenText(e.target.value)}
              className="min-h-[60px] text-sm resize-none"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && redenText.trim()) {
                  e.preventDefault();
                  handleConfirmWithReason();
                }
              }}
            />
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="text-xs"
              >
                Terug
              </Button>
              <Button
                size="sm"
                onClick={handleConfirmWithReason}
                disabled={!redenText.trim()}
                className="ml-auto text-xs"
              >
                Bevestigen
              </Button>
            </div>
          </div>
        ) : (
          /* ─── Option selection step ────────────────────────── */
          <div className="flex flex-col gap-0.5">
            <p className="px-2 py-1 text-xs font-semibold text-neutral-secondary-mid uppercase tracking-wide">
              Beoordeling
            </p>

            {BEOORDELING_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 rounded text-sm w-full text-left',
                  'hover:bg-neutral-100 transition-colors',
                  value === option.value && 'bg-neutral-50',
                )}
              >
                <div
                  className={cn(
                    'w-2.5 h-2.5 rounded-full shrink-0',
                    option.dotColor,
                  )}
                />
                <span className="flex-1 text-neutral-900">{option.label}</span>
                {value === option.value && (
                  <Check size={14} className="text-moss-primary shrink-0" />
                )}
              </button>
            ))}

            {value !== null && (
              <>
                <div className="h-px bg-neutral-100 mx-2 my-0.5" />
                <button
                  type="button"
                  onClick={handleClear}
                  className="flex items-center gap-2 px-2 py-1.5 rounded text-sm w-full text-left hover:bg-neutral-100 transition-colors text-neutral-secondary-mid"
                >
                  Verwijder beoordeling
                </button>
              </>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
